/**
 * O gabarito das telas do mundo — quais campos cada ficha tem, em que ordem e com
 * que aparência.
 *
 * <p>É a tradução do array `COLS` do protótipo (`docs/criacao-do-mundo-prototipo-v2.html`),
 * ajustada ao que a API entrega. Existe para que haja **uma** tela de listagem e **uma**
 * de ficha, em vez de dez de cada: nenhum componente conhece "criatura" pelo nome.
 *
 * <p>Acrescentar um campo aqui, depois de existir no backend, o faz aparecer na tela
 * sem tocar em nenhum componente.
 */
import type { Colecao } from './tipos';

export type TipoCampo =
  | 'texto'
  | 'area'
  | 'selecao'
  | 'check'
  /** Vários booleanos sob um rótulo só — hoje, a Loja do NPC. Ver `subcampos`. */
  | 'checks'
  /** Quadros repetíveis de título + texto — hoje, os diálogos do NPC. */
  | 'quadros'
  | 'multi'
  | 'tags'
  | 'imagem'
  | 'cor'
  | 'iconeDaRegiao'
  | 'subcategoriaDeItem'
  | 'receita'
  | 'relacao'
  | 'relacao1'
  | 'pertenceA'
  | 'pericias'
  /**
   * As perícias do Game Design (`gamedesign/pericias.ts`), e não as sete do mundo: quem
   * ensina Espada ou que quest pede Esquiva precisa da lista fina da Ficha, que o tipo
   * `pericias` — o do hexágono — junta num "combate" só.
   */
  | 'periciasDoDesign'
  | 'derivado'
  | 'relacoesDeRaca';

export interface Campo {
  chave: string;
  rotulo: string;
  tipo: TipoCampo;
  opcoes?: string[];
  /** Coleção alvo de `relacao` e `relacao1`. */
  alvo?: Colecao;
  /** Ocupa a linha inteira do formulário (o `full:1` do protótipo). */
  larga?: boolean;
  /**
   * Ocupa um terço da linha, e não a metade padrão. Existe para campos que são a mesma
   * pergunta feita três vezes, e que por isso se leem melhor lado a lado do que
   * quebrados em duas linhas.
   */
  terco?: boolean;
  /**
   * Ocupa um quarto da linha. Existe para o NPC: Local, Raça, Facção e Quests são as
   * quatro perguntas de <b>vínculo</b> dele — onde está, de quem é, com quem anda e o
   * que oferece —, e a resposta de cada uma é um nome curto. Numa linha só, elas se
   * leem de uma vez; quebradas, viram duas linhas de caixas quase vazias.
   */
  quarto?: boolean;
  /** Os booleanos de um campo `checks`. */
  subcampos?: { chave: string; rotulo: string }[];
  /** Quantos quadros um campo `quadros` aceita. */
  maximo?: number;
  /**
   * Fica no cabeçalho, ao lado do nome, e não no corpo do formulário.
   *
   * <p>Responde <b>onde o campo aparece</b>, e só isso. Se ele é conteúdo do jogador —
   * e portanto entra na caixa dourada — quem responde é o backend, em
   * `ConteudoInGame.java`; ver {@link divisaDaFicha}.
   *
   * <p>As duas perguntas já foram uma só, e tratá-las assim foi o que travou Áreas e
   * Locais: ali o topo tem a Região, o "Pertence a" e o "Área pronta", que estão no
   * cabeçalho por serem <b>estado da ficha</b>, não por serem do jogador.
   */
  cabecalho?: boolean;
  dica?: string;
  /**
   * Campo derivado: mostrado, nunca enviado. Quando traz `vincular`, ganha o seletor
   * que escreve do outro lado — é o que faz a relação ser editável pelas duas telas.
   *
   * <p>`npcNaArea` é o caso especial da Área: ela não tem coluna de NPCs, mas tem um
   * local padrão. Escolher um NPC aqui o coloca nesse local — que é, para quem lê,
   * "este NPC está nesta área".
   */
  vincular?: 'localNaArea' | 'npcNoLocal' | 'npcNaArea' | 'itemNaArea' | 'questNaArea';
}

export interface DefinicaoColecao {
  id: Colecao;
  rotulo: string;
  singular: string;
  icone: string;
  descricao: string;
  /** Campo que guarda a imagem de capa, quando existe. */
  campoImagem?: string;
  /**
   * Desenho especial da ficha. Sem isto, a ficha é o formulário genérico, que é o que
   * quase toda coleção quer.
   *
   * <p>`carta` põe a arte grande à esquerda, com moldura na cor da raridade, e os
   * campos à direita. Existe porque a carta é o único registro do módulo cujo produto
   * final <b>é</b> a imagem: nas outras fichas a imagem ilustra o que está escrito, e
   * aqui o escrito descreve o que está na imagem. Quem edita uma carta precisa vê-la
   * do tamanho em que ela vai existir.
   */
  layout?: 'carta';
  campos: Campo[];
}

const t = (chave: string, rotulo: string, extra: Partial<Campo> = {}): Campo => ({
  chave,
  rotulo,
  tipo: 'texto',
  ...extra,
});
// Todo campo de texto ocupa a linha inteira: com metades, o mesmo formulário tinha
// caixas de tamanhos diferentes e a leitura ficava irregular.
const a = (chave: string, rotulo: string, extra: Partial<Campo> = {}): Campo => ({
  chave,
  rotulo,
  tipo: 'area',
  larga: true,
  ...extra,
});
const rel = (chave: string, rotulo: string, alvo: Colecao, extra: Partial<Campo> = {}): Campo => ({
  chave,
  rotulo,
  tipo: 'relacao',
  alvo,
  larga: true,
  ...extra,
});
const rel1 = (chave: string, rotulo: string, alvo: Colecao, extra: Partial<Campo> = {}): Campo => ({
  chave,
  rotulo,
  tipo: 'relacao1',
  alvo,
  ...extra,
});
const der = (chave: string, rotulo: string, alvo: Colecao, extra: Partial<Campo> = {}): Campo => ({
  chave,
  rotulo,
  tipo: 'derivado',
  alvo,
  larga: true,
  ...extra,
});

/**
 * As cinco raridades do baralho, da mais comum à mais rara.
 *
 * <p>A ordem é a do baralho e não a alfabética: é assim que o filtro se apresenta e é
 * assim que o time fala. Mora aqui, e não solto na tela, porque o mesmo vocabulário
 * serve ao seletor da ficha e ao filtro da listagem — e as duas não podem divergir.
 *
 * <p>A cor não é decoração: numa grade de dezenas de cartas iguais em tamanho, é a
 * mancha de cor que diz de relance quanta coisa rara já existe.
 */
export const RARIDADES_DA_CARTA = ['Comum', 'Incomum', 'Rara', 'Épica', 'Primordial'];

/**
 * As cinco raridades do Item, que <b>não</b> são as mesmas da Carta.
 *
 * <p>Isto é uma divergência conhecida, não um descuido: a carta diz Rara/Épica/
 * Primordial e o item diz Raro/Épico/Lendário. As duas listas já estavam escritas
 * assim quando o filtro chegou, cada uma com fichas gravadas — unificar é uma
 * migration convertendo o que o time já escreveu, e essa é uma decisão de produto,
 * não de código. Enquanto ela não vem, o filtro precisa mostrar exatamente os
 * valores que existem nas fichas de item; qualquer outra lista produziria um filtro
 * que não acha nada.
 *
 * <p>Mora aqui, e não solto na tela, pela mesma razão da lista da carta: o seletor da
 * ficha e o filtro da listagem têm de ler o mesmo vocabulário.
 */
export const RARIDADES_DO_ITEM = ['Comum', 'Incomum', 'Raro', 'Épico', 'Lendário'];

/**
 * A cor de cada raridade, das duas listas no mesmo mapa.
 *
 * <p>Um mapa só, e não um por coleção: a cor responde "quão rara é", e a resposta é a
 * mesma em azul para `Rara` e para `Raro`. Dois mapas divergiriam na primeira vez que
 * alguém acertasse o tom em um deles.
 */
export const COR_DA_RARIDADE: Record<string, string> = {
  Comum: '#8e8875',
  Incomum: '#5f9e6b',
  Rara: '#5a8ec9',
  Raro: '#5a8ec9',
  Épica: '#a273c9',
  Épico: '#a273c9',
  Primordial: '#c9a25a',
  Lendário: '#c9a25a',
};

/**
 * As oito categorias de item, e as subdivisões de cada uma.
 *
 * <p>A lista vazia é o que o formulário lê para esconder o campo Subcategoria — hoje só
 * Dropável e Item de Quest estão assim.
 *
 * <p><b>Equipamento se divide pelo SLOT em que a peça entra</b>, e são os doze da tela
 * de Equipamentos do alpha (V48). Isto já mudou duas vezes, e as três perguntas são
 * diferentes:
 *
 * <ol>
 *   <li>até a V44: <i>o que a peça é</i> — arma, armadura, acessório;</li>
 *   <li>da V44 à V48: <i>quanto do corpo ela ocupa</i> — 1 Mão, 2 Mãos, Torso;</li>
 *   <li>agora: <i>em qual slot ela entra</i> — Mão Direita, Peito, Botas.</li>
 * </ol>
 *
 * <p>Nas duas viradas as fichas que usavam os valores extintos ficaram <b>sem
 * subcategoria</b> de propósito: as réguas não se convertem uma na outra — uma bota e
 * uma calça eram ambas "Pé e Perna" —, e um palpite gravado é pior que um campo em
 * branco visível, que a listagem mostra no balde "Sem subcategoria".
 *
 * <p><b>O que este eixo não diz:</b> que uma arma ocupa as duas mãos. No alpha isso é um
 * selo na peça, não um slot, e aqui não há campo para ele — `duas_maos` fazia esse papel
 * por acidente e saiu na V48.
 *
 * <p>A categoria que a tela chama de <b>Receitas</b> tem id {@code 'craft'}. O rótulo
 * mudou, o id não: trocá-lo obrigaria a reescrever toda ficha gravada e a chave da meta
 * no painel de progresso, sem nada passar a funcionar melhor. Mesma coisa em
 * <b>Tesouro e Artefatos</b>, que continua {@code 'tesouro'}.
 */
export const CATEGORIAS_DE_ITEM = [
  // Os encaixes vêm de cima para baixo no corpo, e não em ordem alfabética: quem
  // preenche a ficha está pensando no boneco, e "Cabeça, Pescoço, Ombro, Torso" se
  // percorre sem ler. As duas empunhaduras abrem a lista porque são a escolha que
  // exclui outra — quem usa 2 Mãos não usa mais nada na outra.
  { id: 'equipamento', rotulo: 'Equipamento',
    /*
     * Os doze slots da tela de Equipamentos do alpha, na ordem em que ela os mostra —
     * da cabeça aos pés, lida linha a linha. O time confere a ficha contra o jogo, e
     * ordem igual é o que torna a conferência possível sem tradução.
     *
     * <p>Seis ids sobreviveram à troca porque o slot é o mesmo, mesmo com outro nome:
     * `torso` mostra "Peito", `cinto` mostra "Cinturão", `mao` mostra "Luvas". Manter o
     * id preserva a ficha já preenchida; o rótulo é do jogo. Os que sumiram estão na V48.
     */
    sub: [
      { id: 'cabeca', rotulo: 'Cabeça' },
      { id: 'pescoco', rotulo: 'Pescoço' },
      { id: 'ombro', rotulo: 'Ombros' },
      { id: 'torso', rotulo: 'Peito' },
      { id: 'costas', rotulo: 'Costas' },
      { id: 'cinto', rotulo: 'Cinturão' },
      { id: 'mao_direita', rotulo: 'Mão Direita' },
      { id: 'mao_esquerda', rotulo: 'Mão Esquerda' },
      { id: 'mao', rotulo: 'Luvas' },
      { id: 'calcas', rotulo: 'Calças' },
      { id: 'botas', rotulo: 'Botas' },
      { id: 'anel', rotulo: 'Anel' },
    ] },
  // Vem logo depois de equipamento porque é o vizinho semântico: as duas são o que o
  // personagem carrega. O que se coleta com elas vem em seguida.
  { id: 'ferramenta', rotulo: 'Ferramenta',
    sub: [
      { id: 'ferramenta_coleta', rotulo: 'Ferramenta de Coleta' },
      { id: 'ferramenta_criacao', rotulo: 'Ferramenta de Criação' },
    ] },
  { id: 'coletavel', rotulo: 'Coletável',
    sub: [
      { id: 'herbalismo', rotulo: 'Herbalismo' },
      { id: 'esfolagem', rotulo: 'Esfolamento' },
      { id: 'mineracao', rotulo: 'Mineração' },
      // O que vem de evento não sai de nenhuma das três perícias — e é justamente o
      // que o time precisa achar rápido quando a temporada acaba.
      { id: 'especial_de_evento', rotulo: 'Especial de Evento' },
    ] },
  // Chamada Craft até aqui. O id continua 'craft' — ver o cabeçalho desta lista.
  { id: 'craft', rotulo: 'Receitas',
    sub: [
      { id: 'tecnomagia', rotulo: 'Tecnomágia' },
      { id: 'alquimia', rotulo: 'Alquimia' },
      { id: 'couraria', rotulo: 'Couraria' },
      { id: 'forja', rotulo: 'Forja' },
    ] },
  // 'ambos' é escolha, e não a falta dela: uma poção de vida serve na luta e fora
  // dela, e isso é diferente de ninguém ter decidido ainda — que segue sendo o vazio.
  { id: 'consumivel', rotulo: 'Consumível',
    sub: [
      { id: 'combate', rotulo: 'Em Combate' },
      { id: 'fora_de_combate', rotulo: 'Fora de Combate' },
      { id: 'ambos', rotulo: 'Ambos' },
      { id: 'criacao', rotulo: 'Criação' },
    ] },
  { id: 'dropavel', rotulo: 'Dropável', sub: [] },
  // As duas do fim se definem pelo que o item faz, e não pelo que ele é: tesouro é o
  // que vale por valor ou por história, e item de quest é o que só vale enquanto a
  // quest corre. Ficam depois de dropável porque a pergunta que respondem é a mesma —
  // "para que serve o que caiu".
  { id: 'tesouro', rotulo: 'Tesouro e Artefatos',
    sub: [
      { id: 'artefato', rotulo: 'Artefatos' },
      { id: 'tesouro_historico', rotulo: 'Tesouros Históricos' },
    ] },
  { id: 'quest', rotulo: 'Item de Quest', sub: [] },
] as const;

export const rotuloDaCategoria = (id?: string | null) =>
  CATEGORIAS_DE_ITEM.find((c) => c.id === id)?.rotulo ?? id ?? '';

export const COLECOES: DefinicaoColecao[] = [
  {
    id: 'regioes',
    rotulo: 'Regiões',
    singular: 'região',
    icone: '▲',
    descricao: '7 regiões, cada uma com 7 áreas. Raças e facções vêm das áreas.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem de capa', tipo: 'imagem', cabecalho: true },
      t('refCultural', 'Referência cultural', { cabecalho: true }),
      { chave: 'corMapa', rotulo: 'Cor no mapa', tipo: 'cor', cabecalho: true },
      a('ambientacao', 'Ambientação'),
      a('historia', 'História'),
      a('relevo', 'Relevo'),
      a('clima', 'Clima'),
      a('fauna', 'Fauna'),
      a('flora', 'Flora'),
      a('hidrografia', 'Hidrografia'),
      a('notas', 'Notas'),
      { chave: 'icone', rotulo: 'Ícone da região', tipo: 'iconeDaRegiao', larga: true },

      // Mesma divisão da Área: o que se escreve ocupa a linha inteira, o que se
      // escolhe fica em duas colunas no fim.
      der('areas', 'Áreas desta região', 'areas', { larga: false }),
      der('racas', 'Raças', 'racas', { larga: false }),
      der('faccoes', 'Facções', 'faccoes', { larga: false }),
    ],
  },
  {
    id: 'areas',
    rotulo: 'Áreas',
    singular: 'área',
    icone: '◆',
    descricao: 'Subdivisão de uma região — o hexágono do mapa.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      // A região fica no topo por ser o lugar da área no mapa — estado da ficha, não
      // conteúdo do jogador. Fora da caixa dourada.
      rel1('regiao', 'Região', 'regioes', { cabecalho: true }),
      // A narrativa fica no cabeçalho, ao lado da imagem: é o único campo escrito
      // para quem joga, e quem abre a área precisa ver primeiro o que o jogador veria.
      {
        chave: 'narrativa',
        rotulo: 'Narrativa da cena',
        tipo: 'area',
        cabecalho: true,
      },
      a('descricao', 'Descrição'),
      a('ambientacao', 'Ambientação'),
      a('geografia', 'Geografia'),
      a('economia', 'Economia'),
      {
        // No cabeçalho, junto do nome: é um estado da área inteira, e não mais um
        // campo de conteúdo entre descrição e economia.
        chave: 'completa',
        rotulo: 'Área',
        tipo: 'check',
        cabecalho: true,
        dica: 'Pronta',
      },
      { chave: 'pericias', rotulo: 'Perícias da área', tipo: 'pericias', larga: true },

      // Daqui para baixo, duas colunas: cada par abaixo ocupa uma linha. São os
      // vínculos, e lê-los lado a lado é mais rápido do que rolar oito blocos largos.
      der('locais', 'Locais', 'locais', { vincular: 'localNaArea', larga: false }),
      der('npcs', 'NPCs', 'npcs', { vincular: 'npcNaArea', larga: false }),
      rel('racas', 'Raças', 'racas', { larga: false }),
      rel('faccoes', 'Facções', 'faccoes', { larga: false }),
      rel('criaturas', 'Criaturas', 'criaturas', { larga: false }),
      der('itens', 'Coletáveis', 'itens', { vincular: 'itemNaArea', larga: false }),
      der('quests', 'Quests', 'quests', { vincular: 'questNaArea', larga: false }),
      rel('eventos', 'Eventos', 'eventos', { larga: false }),
    ],
  },
  {
    id: 'locais',
    rotulo: 'Locais',
    singular: 'local',
    icone: '⌂',
    descricao: 'Lugares dentro de uma área — ou dentro de outro local.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      // Uma escolha só, não duas. Antes havia "Área" e "Dentro de" lado a lado, e nada
      // dizia o que acontecia ao preencher os dois — a taverna podia estar na Área 3 e
      // dentro de um local da Área 5. Agora se escolhe o tipo do pai e depois quem é:
      // um local herda a área de quem o abriga.
      { chave: 'pertenceA', rotulo: 'Pertence a', tipo: 'pertenceA', cabecalho: true },
      // Mesmo lugar e mesmo motivo da narrativa da Área: é o que o jogador lê ao
      // entrar, e quem abre a ficha precisa ver isso antes das notas do time.
      {
        chave: 'narrativa',
        rotulo: 'Narrativa da cena',
        tipo: 'area',
        cabecalho: true,
      },
      a('descricao', 'Descrição'),

      // Duas colunas, como na Área.
      der('filhos', 'Locais aqui dentro', 'locais', { larga: false }),
      der('npcs', 'NPCs', 'npcs', { vincular: 'npcNoLocal', larga: false }),
      rel('tipos', 'Tipos', 'tipos', { larga: false }),
      rel('itens', 'Itens', 'itens', { larga: false }),
      rel('quests', 'Quests', 'quests', { larga: false }),
      rel('eventos', 'Eventos', 'eventos', { larga: false }),
    ],
  },
  {
    id: 'tipos',
    rotulo: 'Tipos',
    singular: 'tipo',
    icone: '⬚',
    descricao: 'Como os locais são classificados: cidade, ruína, masmorra.',
    campos: [a('descricao', 'Descrição', { larga: true })],
  },
  {
    id: 'racas',
    rotulo: 'Raças',
    singular: 'raça',
    icone: '☙',
    descricao: 'Povos e espécies do mundo.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      t('refCultural', 'Referência cultural', { cabecalho: true }),
      {
        chave: 'indole',
        rotulo: 'Índole',
        tipo: 'multi',
        larga: true,
        opcoes: ['Amistosa', 'Neutra', 'Hostil', 'Selvagem', 'Reclusa'],
      },
      {
        chave: 'tipo',
        rotulo: 'Tipo',
        tipo: 'multi',
        larga: true,
        opcoes: ['Humanoide', 'Fera', 'Espírito', 'Híbrido', 'Ancestral', 'Elemental'],
      },
      a('aparencia', 'Aparência'),
      a('biologia', 'Biologia'),
      a('sociedade', 'Sociedade e cultura', { larga: true }),
      { chave: 'tracos', rotulo: 'Traços marcantes', tipo: 'tags', larga: true },
      {
        chave: 'relacoes',
        rotulo: 'Relações com outras raças',
        tipo: 'relacoesDeRaca',
        larga: true,
        alvo: 'racas',
      },
      a('notas', 'Notas', { larga: true }),
    ],
  },
  {
    id: 'faccoes',
    rotulo: 'Facções',
    singular: 'facção',
    icone: '⚑',
    descricao: 'Grupos de poder: guildas, cortes, ordens.',
    campoImagem: 'simbolo',
    campos: [
      { chave: 'simbolo', rotulo: 'Símbolo', tipo: 'imagem', cabecalho: true },
      t('lema', 'Lema', { cabecalho: true }),
      a('descricao', 'Descrição', { larga: true }),
      t('lider', 'Líder'),
      t('base', 'Base / território'),
      {
        chave: 'influencia',
        rotulo: 'Influência',
        tipo: 'selecao',
        opcoes: ['Baixa', 'Média', 'Alta', 'Dominante'],
      },
      rel('aliados', 'Aliados', 'faccoes'),
      rel('inimigos', 'Inimigos', 'faccoes'),
      a('notas', 'Notas', { larga: true }),
    ],
  },
  {
    id: 'criaturas',
    // O rótulo muda junto com o do menu: ele também é o título da página e o texto do
    // seletor de vínculos, e "criaturas e inimigos" cabe bem nos três.
    rotulo: 'Criaturas e Inimigos',
    singular: 'criatura',
    icone: '❦',
    descricao: 'Bestiário: feras, humanoides, espíritos e chefes.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      // A cena fica no in-game, junto da imagem: é o que o jogador lê ao encontrar a
      // criatura. Mesmo lugar e mesma razão da narrativa da Área e do Local.
      { chave: 'narrativa', rotulo: 'Descrição da cena', tipo: 'area', cabecalho: true },

      /*
       * Tipo, classe, temperamento e habitat numa linha só.
       *
       * São as quatro respostas curtas que classificam a criatura, e lidas lado a lado
       * uma dá contexto à outra — "Chefe / Fera mítica / Territorial / Colinas" é uma
       * frase. Empilhadas em blocos largos, cada uma pedia uma parada do olho para
       * dizer uma palavra.
       *
       * O `tipo` saiu do cabeçalho: classificar não é conteúdo do jogador, é como o
       * time organiza o bestiário.
       */
      {
        chave: 'tipo',
        rotulo: 'Tipo',
        tipo: 'selecao',
        quarto: true,
        opcoes: ['Fera', 'Humanoide', 'Espírito', 'Monstro', 'Chefe', 'Outro'],
      },
      {
        chave: 'classe',
        rotulo: 'Classe',
        tipo: 'selecao',
        quarto: true,
        opcoes: [
          'Mamífero', 'Réptil', 'Ave', 'Inseto', 'Anfíbio', 'Peixe',
          'Espírito', 'Humanoide', 'Planta', 'Fera mítica', 'Outro',
        ],
      },
      {
        chave: 'temperamento',
        rotulo: 'Temperamento',
        tipo: 'selecao',
        quarto: true,
        opcoes: ['Agressivo', 'Territorial', 'Neutro', 'Passivo', 'Fugidio'],
      },
      t('habitat', 'Habitat', { quarto: true }),
      a('descricao', 'Descrição', { larga: true }),
      a('acoes', 'Ações e ataques', { larga: true }),
      a('comportamento', 'Comportamento'),
      a('drops', 'Drops / recompensas'),
      // As fichas de item que ela dropa — é o que a página de Perícias lê como "abatendo".
      // O texto acima continua para o que não é item: moedas, partes sem ficha.
      rel('itensDropados', 'Itens que dropa', 'itens'),
      der('areas', 'Onde vive', 'areas'),
    ],
  },
  {
    id: 'itens',
    rotulo: 'Itens',
    singular: 'item',
    icone: '✦',
    descricao: 'Tudo que se pega, veste, fabrica ou consome.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      {
        chave: 'categoria',
        rotulo: 'Categoria',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: CATEGORIAS_DE_ITEM.map((c) => c.id),
      },
      // As opções dependem da categoria — arma não existe em coletável. O componente
      // lê a categoria da própria ficha, por isso o tipo é próprio.
      { chave: 'subcategoria', rotulo: 'Subcategoria', tipo: 'subcategoriaDeItem' },
      {
        chave: 'raridade',
        rotulo: 'Raridade',
        tipo: 'selecao',
        opcoes: RARIDADES_DO_ITEM,
        quarto: true,
      },
      // A mesma escala da Tier List: o T3 de um minério é a dificuldade de colhê-lo, o
      // de uma receita é a de criá-la.
      { chave: 'tier', rotulo: 'Tier', tipo: 'selecao', opcoes: ['T1', 'T2', 'T3', 'T4', 'T5'], quarto: true },
      {
        chave: 'receita',
        rotulo: 'Receita',
        tipo: 'receita',
        larga: true,
      },
      der('usadoEm', 'Usado nas receitas de', 'itens'),
      a('usos', 'Usos'),
      a('descricao', 'Descrição', { larga: true }),
      der('locais', 'Onde encontrar', 'locais'),
      // A região MARCADA, que não é onde o item está — isso continua vindo dos locais,
      // logo acima. Esta responde "de que região já se sabe que ele é", e existe para o
      // intervalo em que a região está decidida e o local ainda não. As duas convivem:
      // a listagem filtra pela união, e a marca vira redundante quando o local chega.
      rel('regioes', 'Regiões', 'regioes', {
        dica: 'Para marcar a região antes de o local existir. Onde o item está de fato vem de "Onde encontrar".',
      }),
      // As outras formas de conseguir o item. Derivadas: quem escreve é a ficha da
      // criatura, do NPC e da quest — a relação mora num lado só (regra 1 do CLAUDE.md).
      der('dropadoPor', 'Dropado por', 'criaturas'),
      der('vendidoPor', 'Vendido por', 'npcs'),
      der('recompensaDe', 'Recompensa das quests', 'quests'),
    ],
  },
  {
    id: 'npcs',
    rotulo: 'NPCs',
    singular: 'NPC',
    icone: '☺',
    descricao: 'Personagens. Cada NPC fica num local — é assim que aparece na área.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      // No cabeçalho, logo abaixo do nome, e não lá embaixo com os campos do corpo: é
      // o que o jogador encontra ao chegar no NPC, então quem abre a ficha precisa ler
      // isso antes das notas do time. Mesmo lugar e mesma razão da narrativa da Área e
      // do Local — os três respondem "o que se vê aqui".
      { chave: 'descricao', rotulo: 'Descrição da Cena', tipo: 'area', cabecalho: true },
      // Os quatro vínculos do NPC numa linha só: onde está, de quem é, com quem anda e
      // o que oferece. A resposta de cada um é um nome curto.
      rel1('local', 'Local', 'locais', { quarto: true }),
      rel1('raca', 'Raça', 'racas', { quarto: true }),
      rel1('faccao', 'Facção', 'faccoes', { quarto: true }),
      rel('quests', 'Quests', 'quests', { larga: false, quarto: true }),
      // As duas únicas áreas de texto em meia largura da ficha inteira. Elas se
      // respondem — quem ele é e como ele age —, e lidas lado a lado uma serve de
      // contraponto à outra.
      a('aparencia', 'Sobre', { larga: false }),
      a('personalidade', 'Personalidade', { larga: false }),
      {
        chave: 'dialogos',
        rotulo: 'Diálogo',
        tipo: 'quadros',
        larga: true,
        maximo: 4,
        dica: 'Até 4',
      },
      {
        chave: 'loja',
        rotulo: 'Loja',
        tipo: 'checks',
        larga: true,
        subcampos: [
          { chave: 'lojaCompra', rotulo: 'Compra do jogador' },
          { chave: 'lojaVenda', rotulo: 'Vende ao jogador' },
        ],
      },
      rel('itensDaLoja', 'Itens da loja', 'itens', {
        dica: 'O que ele vende. Aparece na página de Perícias como "comprando de".',
      }),
      { chave: 'pericias', rotulo: 'Perícias', tipo: 'periciasDoDesign', larga: true },
    ],
  },
  {
    id: 'eventos',
    rotulo: 'Eventos',
    singular: 'evento',
    icone: '✧',
    descricao: 'O que acontece no mundo: festas, migrações, despertares.',
    campoImagem: 'imagem',
    campos: [
      { chave: 'imagem', rotulo: 'Imagem', tipo: 'imagem', cabecalho: true },
      {
        chave: 'tipo',
        rotulo: 'Tipo',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: ['Sazonal', 'Recorrente', 'Único', 'Ritual', 'Catástrofe', 'Outro'],
      },
      a('gatilho', 'Quando acontece', {
        larga: true,
      }),
      a('descricao', 'Descrição', { larga: true }),
      der('areas', 'Áreas onde acontece', 'areas'),
      der('locais', 'Locais onde acontece', 'locais'),
      { chave: 'pericias', rotulo: 'Perícias', tipo: 'periciasDoDesign', larga: true },
    ],
  },
  {
    id: 'quests',
    rotulo: 'Quests',
    singular: 'quest',
    icone: '❖',
    descricao: 'Missões, eventos e jornadas.',
    campos: [
      {
        chave: 'tipo',
        rotulo: 'Tipo',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: ['NPC', 'Evento', 'Jornada', 'Coleta', 'Caça'],
      },
      rel1('giver', 'Quem dá', 'npcs', { cabecalho: true }),
      a('gatilho', 'Gatilho / início', { larga: true }),
      a('etapas', 'Etapas', { larga: true }),
      a('recompensas', 'Recompensas', { larga: true }),
      rel('itensDeRecompensa', 'Itens de recompensa', 'itens', {
        dica: 'As fichas de item que a quest dá. O texto acima fica para o resto: moedas, título, reputação.',
      }),
      rel('locais', 'Onde aparece', 'locais'),
      der('npcs', 'NPCs envolvidos', 'npcs'),
      { chave: 'pericias', rotulo: 'Perícias', tipo: 'periciasDoDesign', larga: true },
    ],
  },
  {
    id: 'cartas',
    rotulo: 'Cartas',
    singular: 'carta',
    icone: '❐',
    descricao: 'O baralho: o que se ganha, se compra e se dropa.',
    campoImagem: 'imagem',
    layout: 'carta',
    campos: [
      { chave: 'imagem', rotulo: 'Arte', tipo: 'imagem', cabecalho: true },
      // Número e pontos são campo de texto de propósito: o servidor lê "" como
      // "apague" e nulo como "não mexa", que é a convenção de todo campo do módulo.
      // Um campo numérico de verdade não teria como dizer "esvazie".
      t('numero', 'Nº no baralho', { cabecalho: true }),
      {
        chave: 'raridade',
        rotulo: 'Raridade',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: RARIDADES_DA_CARTA,
      },
      {
        // A lista é a da planilha do baralho, e não um resumo dela: o campo é texto
        // livre no banco, mas o que o seletor não oferece o time não consegue escolher
        // — e "Outro" apagava a diferença entre forjar, minerar e comprar no Mercado 7.
        chave: 'tipo',
        rotulo: 'Como se obtém',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: [
          'Drop',
          'Drop Dungeon',
          'Quest',
          'Evento',
          'Loja',
          'Mercado 7',
          'Master',
          'Mineração',
          'Coleta',
          /*
           * Continua "Esfolagem" mesmo depois de a PERÍCIA virar Esfolamento, e não é
           * esquecimento: aqui a palavra é opção de "como se obtém", num campo de texto
           * livre cujos valores já estão gravados nas 48 cartas da V19. Renomear a opção
           * não renomearia o que está no banco — só faria as cartas antigas deixarem de
           * casar com o seletor, e o time veria o campo em branco onde escreveu.
           */
          'Esfolagem',
          'Forja',
          'Couraria',
          'Alquimia',
          'Game 1',
          'Game 2',
        ],
      },
      t('pontos', 'Pontos', { cabecalho: true }),
      {
        chave: 'uso',
        rotulo: 'Uso',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: ['Não tem', 'Passiva', 'Ativa'],
      },
      {
        chave: 'recompensa',
        rotulo: 'Recompensa',
        tipo: 'selecao',
        cabecalho: true,
        opcoes: ['Aplicada', 'Pendente'],
      },
      a('texto', 'Texto da carta'),
      a('efeito', 'Efeito'),
    ],
  },
];

export const definicaoDe = (id: Colecao): DefinicaoColecao =>
  COLECOES.find((c) => c.id === id) ?? COLECOES[0];

/** Cores das 7 regiões, na ordem do protótipo. Usadas quando a região ainda não tem a sua. */
export const PALETA_DO_MAPA = [
  '#D9B36A',
  '#3FB0B0',
  '#54B87A',
  '#A87BD0',
  '#CC7A4A',
  '#5B8FB9',
  '#C85B54',
];
